import React, { FC, memo, useState, startTransition } from 'react';
import { ProductViewModel } from '~/lib/data-contract';
import { Typography } from '../typography/typography.component';
import { useTranslation } from '~/shared/utils/translation';
import styles from './size-selector.module.css';
import clsx from 'clsx';

export type Props = Pick<ProductViewModel, 'sizes'> & {
    onSelect: (variantId: string) => void;
    inputName: string;
    selectedVariantId?: string | null;
    showErrorMessage?: boolean;
    stockFrom?: 'web' | 'store';
    sizingText?: string;
    isError?: boolean;
};

const MAX_ITEMS = 13;

const SizeSelector: FC<Props> = memo((props) => {
    const {
        sizes = [],
        onSelect,
        selectedVariantId,
        inputName,
        stockFrom = 'web',
        isError = false,
        sizingText,
    } = props;

    const [showAll, setShowAll] = useState(false);
    const remaining = sizes.length - MAX_ITEMS;

    const { translate } = useTranslation();

    return (
        <div className={clsx(styles.root, { [styles.error]: isError })}>
            {sizingText && (
                <div className={styles.sizingText}>
                    <Typography variant="bodySm">{translate('productDetails.size')}</Typography>
                    <Typography variant="bodyXs">{sizingText}</Typography>
                </div>
            )}

            <Typography as="div" variant="caption" uppercase>
                {translate('productDetails.size')}
            </Typography>
            <div className={styles.options}>
                {sizes
                    ?.slice(0, showAll ? undefined : MAX_ITEMS)
                    .map(({ size, variantId, inStockInStore, inStockOnWeb }, i) => {
                        const inputIdName = `${inputName}-${size}`;
                        const disabled = stockFrom === 'web' ? !inStockOnWeb : !inStockInStore;
                        return (
                            <label
                                htmlFor={inputIdName}
                                className={styles.option}
                                key={`${variantId}-${size}-${i}`}
                            >
                                <input
                                    className={styles.radio}
                                    key={variantId}
                                    name={inputName}
                                    id={inputIdName}
                                    type="radio"
                                    onChange={() =>
                                        startTransition(() => onSelect(variantId || ''))
                                    }
                                    checked={variantId === selectedVariantId}
                                    disabled={disabled}
                                    value={size}
                                />
                                <span className={styles.sizeLabel}>{size}</span>
                            </label>
                        );
                    })}
                {!showAll && sizes.length > MAX_ITEMS && (
                    <button className={styles.showMoreButton} onClick={() => setShowAll(true)}>
                        +{remaining}
                    </button>
                )}
            </div>

            {isError && (
                <Typography variant="bodyXs" color="red">
                    {translate('productDetails.selectSize')}
                </Typography>
            )}
        </div>
    );
});

SizeSelector.displayName = 'SizeSelector';
export { SizeSelector };
